import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/contexts/AuthContext';
import BackNavButton from '@/components/BackNavButton';
import { toast } from 'sonner';
import { Loader2, RefreshCw, ShieldCheck, AlertTriangle, ServerCrash, CheckCircle2 } from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip as ReTooltip, ResponsiveContainer, CartesianGrid,
} from 'recharts';

const RANGES = [
  ['24h', '24 hours'],
  ['7d', '7 days'],
  ['30d', '30 days'],
  ['90d', '90 days'],
];

const STAGE_LABEL = {
  sightengine: 'Sightengine',
  secondary: 'Secondary Age',
  face_match: 'Face Match',
};

function Kpi({ label, value, sub, color = '#0F0F10', icon: Icon, testId }) {
  return (
    <div className="bg-[var(--surface-2)] rounded-2xl p-3 border border-[var(--hairline)]" data-testid={testId}>
      <div className="flex items-center gap-1.5 mb-1">
        {Icon && <Icon size={11} style={{ color }} />}
        <p className="text-[10px] uppercase tracking-wider font-bold text-[#8A8A93]">{label}</p>
      </div>
      <p className="font-display text-2xl font-[700] tabular-nums" style={{ color }}>{value}</p>
      {sub && <p className="text-[10px] text-[#8A8A93] mt-0.5">{sub}</p>}
    </div>
  );
}

function Section({ title, children, action }) {
  return (
    <section className="px-5 mt-6">
      <div className="flex items-center gap-2 mb-3">
        <h2 className="text-[12px] uppercase tracking-wider font-bold text-[#4B4B52]">{title}</h2>
        <div className="ml-auto">{action}</div>
      </div>
      {children}
    </section>
  );
}

function ActionBadge({ action }) {
  const map = {
    accept: { bg: '#DCFCE7', fg: '#166534', label: 'ACCEPT' },
    reject: { bg: '#FEE2E2', fg: '#991B1B', label: 'REJECT' },
    block:  { bg: '#FEF3C7', fg: '#92400E', label: 'BLOCK' },
    error:  { bg: '#E5E7EB', fg: '#374151', label: 'ERROR' },
  };
  const s = map[action] || { bg: '#F3F4F6', fg: '#374151', label: (action || '—').toUpperCase() };
  return (
    <span className="inline-block px-1.5 py-0.5 rounded text-[9px] font-bold tracking-wider" style={{ background: s.bg, color: s.fg }}>
      {s.label}
    </span>
  );
}

function fmtTime(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
}

export default function AdminVerifySignals() {
  const navigate = useNavigate();
  const [authChecked, setAuthChecked] = useState(false);
  const [range, setRange] = useState('7d');
  const [data, setData] = useState(null);
  const [attempts, setAttempts] = useState(null);
  const [attemptFilter, setAttemptFilter] = useState({ action: '', stage: '' });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/admin/me').then(() => setAuthChecked(true))
      .catch(() => { toast.error('Admin access required'); navigate('/discover', { replace: true }); });
  }, [navigate]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [signals, list] = await Promise.all([
        api.get(`/admin/verify-signals?range=${range}`),
        api.get(`/admin/verify-signals/attempts?range=${range}` +
          (attemptFilter.action ? `&action=${attemptFilter.action}` : '') +
          (attemptFilter.stage ? `&stage=${attemptFilter.stage}` : '') +
          `&limit=50`),
      ]);
      setData(signals.data);
      setAttempts(list.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load verification signals');
    } finally {
      setLoading(false);
    }
  }, [range, attemptFilter.action, attemptFilter.stage]);

  useEffect(() => { if (authChecked) load(); }, [authChecked, load]);

  if (!authChecked || (loading && !data)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <Loader2 className="w-7 h-7 animate-spin text-[#B91C1C]" />
      </div>
    );
  }

  const k = data?.kpis || {};
  const stageRows = data?.by_stage || [];
  const reasons = data?.rejection_reasons || [];
  const hist = (data?.similarity_histogram || []).map(b => ({ bucket: b.bucket, Attempts: b.count }));
  const sim = data?.similarity_stats || {};

  return (
    <div className="min-h-screen bg-white pb-16" data-testid="admin-verify-signals-page">
      {/* Header */}
      <div className="px-5 pt-8 pb-4 flex items-center gap-3 sticky top-0 bg-white/95 backdrop-blur-md z-20 border-b border-[var(--hairline)]">
        <BackNavButton />
        <h1 className="font-brand text-[26px] font-[600] tracking-tight">Verify Signals</h1>
        <button
          onClick={load} disabled={loading}
          data-testid="verify-signals-refresh-btn"
          className="ml-auto p-2 rounded-full hover:bg-black/[0.04]"
          aria-label="Refresh"
        >
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      {/* Range selector */}
      <div className="px-5 pt-4">
        <div className="inline-flex gap-1 rounded-full border border-[var(--hairline)] p-1 bg-[var(--surface-2)]">
          {RANGES.map(([r, label]) => (
            <button
              key={r}
              onClick={() => setRange(r)}
              data-testid={`verify-signals-range-${r}`}
              className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${
                range === r ? 'bg-[#0F0F10] text-white' : 'text-[#4B4B52] hover:text-[#0F0F10]'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* KPI grid */}
      <Section title="Pipeline Overview">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
          <Kpi label="Total Attempts" value={k.total ?? 0} testId="kpi-total" />
          <Kpi label="Accepted" value={k.accepted ?? 0} sub={`${k.accept_rate ?? 0}% accept rate`} color="#166534" icon={CheckCircle2} testId="kpi-accepted" />
          <Kpi label="Rejected" value={k.rejected ?? 0} color="#991B1B" icon={AlertTriangle} testId="kpi-rejected" />
          <Kpi label="Blocked" value={k.blocked ?? 0} sub="e.g. minor detected" color="#92400E" icon={ShieldCheck} testId="kpi-blocked" />
          <Kpi label="Provider Errors" value={k.errored ?? 0} sub="503s / quota / outage" color="#B91C1C" icon={ServerCrash} testId="kpi-errored" />
        </div>
      </Section>

      {/* By stage table */}
      <Section title="By Stage">
        <div className="rounded-2xl border border-[var(--hairline)] overflow-hidden">
          <table className="w-full text-sm" data-testid="verify-signals-by-stage">
            <thead className="bg-[var(--surface-2)]">
              <tr className="text-[10px] uppercase tracking-wider text-[#4B4B52]">
                <th className="text-left px-4 py-2">Stage</th>
                <th className="text-right px-3 py-2">Accept</th>
                <th className="text-right px-3 py-2">Reject</th>
                <th className="text-right px-3 py-2">Block</th>
                <th className="text-right px-3 py-2">Error</th>
              </tr>
            </thead>
            <tbody>
              {stageRows.length === 0 ? (
                <tr><td colSpan={5} className="px-4 py-6 text-center text-[#8A8A93] italic text-xs">No verification attempts in this range.</td></tr>
              ) : stageRows.map((s) => (
                <tr key={s.stage} className="border-t border-[var(--hairline)]" data-testid={`stage-row-${s.stage}`}>
                  <td className="px-4 py-2 font-medium">{STAGE_LABEL[s.stage] || s.stage}</td>
                  <td className="text-right px-3 py-2 tabular-nums text-[#166534]">{s.accept}</td>
                  <td className="text-right px-3 py-2 tabular-nums text-[#991B1B]">{s.reject}</td>
                  <td className="text-right px-3 py-2 tabular-nums text-[#92400E]">{s.block}</td>
                  <td className="text-right px-3 py-2 tabular-nums text-[#B91C1C]">{s.error}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      {/* Rejection Reasons */}
      <Section title="Top Rejection Reasons">
        <div className="rounded-2xl border border-[var(--hairline)] p-4">
          {reasons.length === 0 ? (
            <p className="text-xs italic text-[#8A8A93]">No rejections in this range.</p>
          ) : (
            <ul className="space-y-1.5" data-testid="rejection-reasons-list">
              {reasons.map((r) => {
                const max = reasons[0].count || 1;
                const pct = Math.max(4, Math.round((r.count / max) * 100));
                return (
                  <li key={r.reason} className="flex items-center gap-3 text-xs">
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-0.5">
                        <span className="font-mono text-[#0F0F10] text-[11px]">{r.reason}</span>
                        <span className="tabular-nums text-[#4B4B52] font-semibold">{r.count}</span>
                      </div>
                      <div className="h-1.5 rounded-full bg-[var(--surface-2)] overflow-hidden">
                        <div className="h-full bg-[#B91C1C]" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </Section>

      {/* Similarity histogram */}
      <Section
        title="Rekognition Similarity Distribution"
        action={sim.count ? (
          <span className="text-[10px] text-[#8A8A93] tabular-nums">
            n={sim.count} · avg {sim.avg} · min {sim.min} · max {sim.max}
          </span>
        ) : null}
      >
        <div className="rounded-2xl border border-[var(--hairline)] p-3">
          {sim.count ? (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={hist} data-testid="similarity-histogram">
                <CartesianGrid strokeDasharray="3 3" stroke="#EEE" />
                <XAxis dataKey="bucket" fontSize={10} stroke="#8A8A93" />
                <YAxis allowDecimals={false} fontSize={10} stroke="#8A8A93" />
                <ReTooltip />
                <Bar dataKey="Attempts" fill="#1D4ED8" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-xs italic text-[#8A8A93] py-6 text-center">No face-match attempts recorded in this range.</p>
          )}
        </div>
      </Section>

      {/* Recent Attempts drill-down */}
      <Section
        title="Recent Attempts"
        action={
          <div className="flex items-center gap-2">
            <select
              value={attemptFilter.stage}
              onChange={(e) => setAttemptFilter((f) => ({ ...f, stage: e.target.value }))}
              data-testid="attempts-stage-filter"
              className="text-[11px] border border-[var(--hairline)] rounded-md px-2 py-1 bg-white"
            >
              <option value="">All stages</option>
              <option value="sightengine">Sightengine</option>
              <option value="secondary">Secondary Age</option>
              <option value="face_match">Face Match</option>
            </select>
            <select
              value={attemptFilter.action}
              onChange={(e) => setAttemptFilter((f) => ({ ...f, action: e.target.value }))}
              data-testid="attempts-action-filter"
              className="text-[11px] border border-[var(--hairline)] rounded-md px-2 py-1 bg-white"
            >
              <option value="">All actions</option>
              <option value="accept">Accepted</option>
              <option value="reject">Rejected</option>
              <option value="block">Blocked</option>
              <option value="error">Error</option>
            </select>
          </div>
        }
      >
        <div className="rounded-2xl border border-[var(--hairline)] overflow-hidden">
          <table className="w-full text-xs" data-testid="attempts-table">
            <thead className="bg-[var(--surface-2)]">
              <tr className="text-[10px] uppercase tracking-wider text-[#4B4B52]">
                <th className="text-left px-3 py-2">When</th>
                <th className="text-left px-3 py-2">User</th>
                <th className="text-left px-3 py-2">Stage</th>
                <th className="text-left px-3 py-2">Action</th>
                <th className="text-left px-3 py-2">Signal / Reason</th>
              </tr>
            </thead>
            <tbody>
              {!attempts || attempts.items.length === 0 ? (
                <tr><td colSpan={5} className="px-4 py-6 text-center text-[#8A8A93] italic">No attempts match the current filters.</td></tr>
              ) : attempts.items.map((row, i) => {
                const stage = (row.target || '').replace('verify_v3_', '');
                const seReasons = row.sightengine?.reasons?.join(', ');
                const sim = row.rekognition?.similarity;
                const detail = seReasons
                  || (sim != null ? `similarity ${Number(sim).toFixed(2)}` : null)
                  || row.reason
                  || '—';
                return (
                  <tr key={i} className="border-t border-[var(--hairline)] hover:bg-[var(--surface-2)]/60" data-testid={`attempt-row-${i}`}>
                    <td className="px-3 py-2 text-[#4B4B52] tabular-nums whitespace-nowrap">{fmtTime(row.created_at)}</td>
                    <td className="px-3 py-2">
                      <div className="font-medium text-[#0F0F10]">{row.user?.display_name || row.user?.name || '—'}</div>
                      <div className="text-[10px] text-[#8A8A93]">{row.user?.email || row.user_id || '—'}</div>
                    </td>
                    <td className="px-3 py-2">{STAGE_LABEL[stage] || stage}</td>
                    <td className="px-3 py-2"><ActionBadge action={row.action} /></td>
                    <td className="px-3 py-2 font-mono text-[11px] text-[#4B4B52]">{detail}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {attempts?.total > (attempts?.items?.length || 0) && (
          <p className="text-[10px] text-[#8A8A93] mt-2 text-center">Showing {attempts.items.length} of {attempts.total} attempts. Refine filters or narrow the range.</p>
        )}
      </Section>
    </div>
  );
}
